"use client";

import React from "react";
import { Activity, Cpu, Network, Search, ShieldCheck, Terminal } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "TALK. CONNECT NOW.",
  description: "Launch your search with PocketPal. Our AI voice agent captures your specific industrial or office requirements in seconds.",
  icon: Search
}, {
  title: "MATCH. SELECT SPACE.",
  description: "Receive a curated selection of properties. Data-backed options that perfectly align with your operational growth goals.",
  icon: Cpu
}, {
  title: "MOVE. SCALE EMPIRE.",
  description: "Move into your new headquarters. Zero cost to you, maximum efficiency for your business. The future of CRE is here.",
  icon: ShieldCheck
}];
function RoadmapStep({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: true
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="124601" ref={ref} className={`relative flex items-center justify-between md:justify-normal w-full mb-16 last:mb-0 group ${isEven ? "md:flex-row-reverse" : "md:flex-row"}`}>
      {}
      <div className={`absolute left-8 md:left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border-4 bg-background transition-all duration-700 z-20 ${isVisible ? "border-primary bg-primary shadow-[0_0_30px_#3CC5F3] scale-125" : "border-muted-foreground/30 scale-75"}`}>
        {}
        {isVisible && <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-40" />}
      </div>

      {}
      <div className={`hidden md:block absolute top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r transition-all duration-1000 delay-300 z-10 ${isVisible ? "w-16 opacity-100" : "w-0 opacity-0"} ${isEven ? "right-[calc(50%+1.5rem)] from-transparent to-primary/60" : "left-[calc(50%+1.5rem)] from-primary/60 to-transparent"}`} />

      {}
      <div className={`w-full pl-20 md:pl-0 md:w-[calc(50%-4rem)] transition-all duration-700 ease-out transform ${isVisible ? "opacity-100 translate-x-0" : `opacity-0 ${isEven ? "md:-translate-x-16 translate-x-8" : "md:translate-x-16 translate-x-8"}`}`}>
        <Card className="bg-[#0D1B2A]/60 backdrop-blur-2xl border-primary/20 hover:border-primary transition-all duration-500 relative overflow-hidden group-hover:shadow-[0_0_40px_rgba(60,197,243,0.15)]">
          {}
          <div className="absolute -inset-1/2 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl pointer-events-none" />
          
          <CardHeader className="pb-4 relative z-10">
            <div className="flex items-start gap-4">
              <div className="p-4 rounded-xl bg-primary/10 border border-primary/30 text-primary shrink-0 transition-transform duration-500 group-hover:scale-110">
                <step.icon className="w-8 h-8" />
              </div>
              <div>
                <div className="text-xs font-mono text-primary font-bold mb-1 tracking-[0.2em]">
                  PHASE 0{index + 1}
                </div>
                <CardTitle className="text-2xl font-black tracking-tighter uppercase italic">{step.title}</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <p className="text-foreground/80 leading-snug font-medium">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheImplementationRoadmap() {
  return <section id="talk-match-move-in" className="dark bg-[#0D1B2A] text-white py-24 md:py-40 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(60,197,243,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(60,197,243,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_50%,transparent_100%)] opacity-20 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-24 md:mb-40">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 tracking-tighter uppercase italic">
            Talk. Match. <span className="text-primary drop-shadow-[0_0_20px_rgba(60,197,243,0.6)]">Move In.</span>
          </h2>
          <p className="text-foreground/70 max-w-2xl mx-auto text-xl md:text-2xl font-medium">
            A high-velocity transition protocol designed to secure your commercial headquarters with zero friction and zero cost.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-24 w-1 bg-gradient-to-b from-transparent via-primary/30 to-primary -translate-x-1/2 z-0" />

          {}
          <div className="relative py-10">
            {steps.map((step, i) => <div key={i} data-index={i}>
                <RoadmapStep step={step} index={i} />
              </div>)}
          </div>

          {}
          
        </div>
      </div>
    </section>;
}